// i18n 초기화 — 한국어(기본) / 영어. LiveSQL(frontend/src/i18n/index.js)과 같은 구성이다.
//
// [fallbackLng 를 'ko' 로 두는 이유]
// 영어 번역이 비어 있는 키는 한국어가 그대로 나온다. 화면이 깨지지 않으면서 미번역 지점이 눈에 띈다.
// 반대로 fallback 을 'en' 으로 두면 번역이 덜 된 화면에서 한국어 사용자가 영어를 보게 된다 — 회귀다.
//
// [언어 결정 순서]
// 헤더에서 고른 값(localStorage) → 브라우저 언어 → 한국어. 화면 전환(2단계)이 끝나 영어 브라우저 사용자도
// 섞이지 않은 화면을 보게 되어 브라우저 언어를 따른다(LiveSQL 과 같게). 남은 한국어는 서버 문구(3단계)와
// 도움말(4단계)이다.
//
// [i18next 22 를 쓰는 이유]
// LiveSQL 은 i18next 26 이지만 그 타입 정의는 TypeScript 5 를 요구한다. 이 저장소는 react-scripts 5 에
// 묶여 TypeScript 4.9 라, 호출 모양(useTranslation · t('ns:key', { … }))이 같은 22 를 쓴다.
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import koCommon from './locales/ko/common.json';
import koWorkflow from './locales/ko/workflow.json';
import enCommon from './locales/en/common.json';
import enWorkflow from './locales/en/workflow.json';
import koAssistant from './locales/ko/assistant.json';
import enAssistant from './locales/en/assistant.json';
import koBulk from './locales/ko/bulk.json';
import enBulk from './locales/en/bulk.json';
import koWizard from './locales/ko/wizard.json';
import enWizard from './locales/en/wizard.json';
import koDatadef from './locales/ko/datadef.json';
import enDatadef from './locales/en/datadef.json';
import koDialog from './locales/ko/dialog.json';
import enDialog from './locales/en/dialog.json';
import koConnection from './locales/ko/connection.json';
import enConnection from './locales/en/connection.json';
import koExecution from './locales/ko/execution.json';
import enExecution from './locales/en/execution.json';
import koProperty from './locales/ko/property.json';
import enProperty from './locales/en/property.json';
import koLayout from './locales/ko/layout.json';
import koError from './locales/ko/error.json';
import enLayout from './locales/en/layout.json';
import enError from './locales/en/error.json';
import koMapping from './locales/ko/mapping.json';
import enMapping from './locales/en/mapping.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'ko', label: '한국어' },
  { code: 'en', label: 'English' },
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

export const NAMESPACES = ['common', 'workflow', 'assistant', 'bulk', 'wizard', 'datadef', 'dialog', 'connection', 'execution', 'property', 'layout', 'error', 'mapping'];

const STORAGE_KEY = 'openflow_lang';

const isSupported = (code: string | null | undefined): code is LanguageCode =>
  !!code && SUPPORTED_LANGUAGES.some((l) => l.code === code);

/** 저장값(헤더에서 고른 언어) → 브라우저 언어 → 'ko' */
export function detectLanguage(): LanguageCode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isSupported(saved)) return saved;
  } catch { /* 시크릿 모드 등에서 접근 불가 — 브라우저 언어로 */ }
  if (typeof navigator !== 'undefined') {
    const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const l of langs) {
      const code = String(l || '').toLowerCase().split('-')[0];
      if (isSupported(code)) return code;
    }
  }
  return 'ko';
}

export function setLanguage(code: string): void {
  if (!isSupported(code)) return;
  i18n.changeLanguage(code);
  try { localStorage.setItem(STORAGE_KEY, code); } catch { /* 저장 실패는 무시 — 이번 세션에는 적용됨 */ }
  if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', code);
}

i18n
  .use(initReactI18next)
  .init({
    resources: {
      ko: { common: koCommon, workflow: koWorkflow, assistant: koAssistant, bulk: koBulk, wizard: koWizard, datadef: koDatadef, dialog: koDialog, connection: koConnection, execution: koExecution, property: koProperty, layout: koLayout, error: koError, mapping: koMapping },
      en: { common: enCommon, workflow: enWorkflow, assistant: enAssistant, bulk: enBulk, wizard: enWizard, datadef: enDatadef, dialog: enDialog, connection: enConnection, execution: enExecution, property: enProperty, layout: enLayout, error: enError, mapping: enMapping },
    },
    lng: detectLanguage(),
    fallbackLng: 'ko',
    ns: NAMESPACES,
    defaultNS: 'common',
    interpolation: { escapeValue: false },   // React 가 이미 이스케이프한다
    returnEmptyString: false,                // 빈 문자열이면 fallback(한국어)으로
    returnNull: false,
  });

if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('lang', i18n.language);
}

export default i18n;
